import { useState } from 'react';
import { type StripCaptureMode } from '../../utils/roadStripSurvey';

const labelClass = 'block text-xs font-medium text-gray-600 dark:text-neutral-400 mb-1';

type LayoutStepProps = {
  onSelectSingle: () => void;
  onSelectDivided: () => void;
};

export function RoadLayoutStep({ onSelectSingle, onSelectDivided }: LayoutStepProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-neutral-400">
        What type of carriageway are you mapping? Layout is fixed for this contribution.
      </p>
      <div className="grid gap-3">
        <button
          type="button"
          onClick={onSelectSingle}
          className="text-left rounded-xl border-2 border-gray-200 dark:border-neutral-600 p-4 hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
        >
          <p className="font-semibold text-gray-900 dark:text-neutral-100">Single carriageway</p>
          <p className="text-xs text-gray-600 dark:text-neutral-400 mt-1">
            One motor strip — trace the centre or an outer kerb.
          </p>
        </button>
        <button
          type="button"
          onClick={onSelectDivided}
          className="text-left rounded-xl border-2 border-gray-200 dark:border-neutral-600 p-4 hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
        >
          <p className="font-semibold text-gray-900 dark:text-neutral-100">Divided carriageway</p>
          <p className="text-xs text-gray-600 dark:text-neutral-400 mt-1">
            A divide or median separates two or more strips.
          </p>
        </button>
      </div>
    </div>
  );
}

type DividedStrategyId = 'from_divide' | 'trace_centrelines' | 'trace_outer_kerbs';

type CapturePlanStepProps = {
  carriagewayCount: number;
  singleStripCaptureMode: StripCaptureMode;
  onSingleStripCaptureModeChange: (m: StripCaptureMode) => void;
  stripACaptureMode: StripCaptureMode;
  onStripACaptureModeChange: (m: StripCaptureMode) => void;
  stripBCaptureMode: StripCaptureMode;
  onStripBCaptureModeChange: (m: StripCaptureMode) => void;
  validationError: string | null;
  onContinue: () => void;
};

function strategyCardClass(selected: boolean): string {
  return `flex gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
    selected
      ? 'border-primary-400 dark:border-primary-600 bg-primary-50/80 dark:bg-primary-950/25 ring-1 ring-primary-400/30'
      : 'border-gray-200 dark:border-neutral-600 hover:border-gray-300 dark:hover:border-neutral-500'
  }`;
}

const SINGLE_STRATEGY_OPTIONS = [
  {
    mode: 'trace_centerline' as const,
    title: 'Centreline',
    badge: 'Default',
    description:
      'Trace along the centre of the paved carriageway. Coordal uses this line as the navigation centreline and derives lane axes and kerbside facilities from it.',
  },
  {
    mode: 'trace_outer_kerb' as const,
    title: 'Outer kerb',
    badge: null as string | null,
    description:
      'Trace the property-side outer kerb (building line). Coordal derives the centreline using the strip width you enter in Details.',
    propertyNote:
      'Walk the property kerb with the carriageway on your right. Trace in the direction you walked along that building line.',
  },
] as const;

const DIVIDED_STRATEGY_OPTIONS: {
  id: DividedStrategyId;
  title: string;
  badge: string | null;
  description: string;
  markingSummary: string;
}[] = [
  {
    id: 'from_divide',
    title: 'From the divide',
    badge: 'Default',
    description:
      'Trace the median or divide. Coordal derives each carriageway\'s centreline from the divide geometry and the entered widths. No separate carriageway centreline traces are required.',
    markingSummary: 'Guided marking: divide only.',
  },
  {
    id: 'trace_centrelines',
    title: 'Trace each centreline',
    badge: null,
    description:
      'Trace the centre of each paved carriageway independently. Coordal uses each trace as that carriageway\'s navigation centreline.',
    markingSummary: 'Guided marking: centreline A, then centreline B.',
  },
  {
    id: 'trace_outer_kerbs',
    title: 'Trace outer kerbs',
    badge: null,
    description:
      'Trace the outer reference edge of each carriageway (property side). Coordal derives each centreline using the corresponding strip width from Details.',
    markingSummary: 'Guided marking: outer edge A, then outer edge B.',
  },
];

const STRIP_MODE_OPTIONS: { value: StripCaptureMode; label: string }[] = [
  { value: 'derive_from_divide', label: 'From divide' },
  { value: 'trace_centerline', label: 'Centreline' },
  { value: 'trace_outer_kerb', label: 'Outer kerb (property side)' },
];

function inferSharedDividedStrategy(
  modeA: StripCaptureMode,
  modeB: StripCaptureMode,
): DividedStrategyId | null {
  if (modeA === 'derive_from_divide' && modeB === 'derive_from_divide') return 'from_divide';
  if (modeA === 'trace_centerline' && modeB === 'trace_centerline') return 'trace_centrelines';
  if (modeA === 'trace_outer_kerb' && modeB === 'trace_outer_kerb') return 'trace_outer_kerbs';
  return null;
}

function applyDividedStrategy(
  id: DividedStrategyId,
  onA: (m: StripCaptureMode) => void,
  onB: (m: StripCaptureMode) => void,
): void {
  if (id === 'from_divide') {
    onA('derive_from_divide');
    onB('derive_from_divide');
  } else if (id === 'trace_centrelines') {
    onA('trace_centerline');
    onB('trace_centerline');
  } else {
    onA('trace_outer_kerb');
    onB('trace_outer_kerb');
  }
}

export function RoadCapturePlanStep(props: CapturePlanStepProps) {
  const {
    carriagewayCount,
    singleStripCaptureMode,
    stripACaptureMode,
    stripBCaptureMode,
    onSingleStripCaptureModeChange,
    onStripACaptureModeChange,
    onStripBCaptureModeChange,
    validationError,
    onContinue,
  } = props;
  const divided = carriagewayCount >= 2;
  const sharedFromModes = inferSharedDividedStrategy(stripACaptureMode, stripBCaptureMode);
  const [customizeDivided, setCustomizeDivided] = useState(() => divided && sharedFromModes == null);
  const [dividedStrategy, setDividedStrategy] = useState<DividedStrategyId>(
    sharedFromModes ?? 'from_divide',
  );

  const activeDividedStrategy = customizeDivided ? null : (sharedFromModes ?? dividedStrategy);

  const showDivideDerivedNote =
    customizeDivided
    && (stripACaptureMode === 'derive_from_divide' || stripBCaptureMode === 'derive_from_divide');

  const selectDividedStrategy = (id: DividedStrategyId) => {
    setCustomizeDivided(false);
    setDividedStrategy(id);
    applyDividedStrategy(id, onStripACaptureModeChange, onStripBCaptureModeChange);
  };

  return (
    <div className="space-y-4">
      {!divided ? (
        <>
          <p className="text-sm text-gray-600 dark:text-neutral-400">
            Choose how the motor carriageway geometry is established. Draw vs GPS trace is set on the previous step.
            Strip width, lane count, and optional per-lane widths are configured in Details.
          </p>
          <div className="space-y-2">
            <label className={labelClass}>Geometry strategy</label>
            <div className="grid gap-2" role="radiogroup" aria-label="Geometry strategy">
              {SINGLE_STRATEGY_OPTIONS.map((opt) => {
                const selected = singleStripCaptureMode === opt.mode;
                return (
                  <label key={opt.mode} className={strategyCardClass(selected)}>
                    <input
                      type="radio"
                      name="single-geometry-strategy"
                      checked={selected}
                      onChange={() => onSingleStripCaptureModeChange(opt.mode)}
                      className="mt-1 shrink-0 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-neutral-100">
                          {opt.title}
                        </span>
                        {opt.badge ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300 bg-primary-100/80 dark:bg-primary-950/40 px-1.5 py-0.5 rounded">
                            {opt.badge}
                          </span>
                        ) : null}
                      </span>
                      <span className="block text-xs text-gray-600 dark:text-neutral-400 mt-1 leading-relaxed">
                        {opt.description}
                      </span>
                      {selected && opt.mode === 'trace_outer_kerb' && 'propertyNote' in opt ? (
                        <span className="block text-xs text-primary-800 dark:text-primary-200 mt-2 bg-primary-50/90 dark:bg-primary-950/35 rounded-lg px-2.5 py-2 leading-relaxed">
                          {opt.propertyNote}
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600 dark:text-neutral-400">
            Choose how the divided carriageway geometry is established. Draw vs GPS trace is set on the previous step.
            Strip widths, lane counts, and optional per-lane widths are configured in Details.
          </p>
          <div className="space-y-2">
            <label className={labelClass}>Geometry strategy</label>
            <div className="grid gap-2" role="radiogroup" aria-label="Divided geometry strategy">
              {DIVIDED_STRATEGY_OPTIONS.map((opt) => {
                const selected = !customizeDivided && activeDividedStrategy === opt.id;
                return (
                  <label key={opt.id} className={strategyCardClass(selected)}>
                    <input
                      type="radio"
                      name="divided-geometry-strategy"
                      checked={selected}
                      onChange={() => selectDividedStrategy(opt.id)}
                      className="mt-1 shrink-0 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-neutral-100">
                          {opt.title}
                        </span>
                        {opt.badge ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300 bg-primary-100/80 dark:bg-primary-950/40 px-1.5 py-0.5 rounded">
                            {opt.badge}
                          </span>
                        ) : null}
                      </span>
                      <span className="block text-xs text-gray-600 dark:text-neutral-400 mt-1 leading-relaxed">
                        {opt.description}
                      </span>
                      {selected ? (
                        <span className="block text-xs text-gray-500 dark:text-neutral-400 mt-2">
                          {opt.markingSummary}
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setCustomizeDivided((v) => !v)}
              className="text-xs font-medium text-primary-600 dark:text-primary-400 underline underline-offset-2"
            >
              {customizeDivided ? 'Use shared strategy' : 'Customise each carriageway'}
            </button>

            {customizeDivided ? (
              <div className="space-y-3 rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/50 dark:bg-neutral-800/30 p-3">
                <p className="text-xs text-gray-600 dark:text-neutral-400">
                  Mixed layouts (e.g. one strip from the divide, one traced). Unusual — use only when the road layout
                  requires it.
                </p>
                {(['A', 'B'] as const).map((side) => {
                  const value = side === 'A' ? stripACaptureMode : stripBCaptureMode;
                  const onChange = side === 'A' ? onStripACaptureModeChange : onStripBCaptureModeChange;
                  return (
                    <label key={side} className="block space-y-1">
                      <span className="text-xs font-semibold text-gray-800 dark:text-neutral-200">
                        Carriageway {side}
                      </span>
                      <select
                        value={value}
                        onChange={(e) => onChange(e.target.value as StripCaptureMode)}
                        className="w-full rounded-lg border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 py-2 text-sm"
                      >
                        {STRIP_MODE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                })}
                {showDivideDerivedNote ? (
                  <p className="text-xs text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-950/30 rounded-lg px-2.5 py-2">
                    Strips set to &ldquo;From divide&rdquo; are derived from the divide trace and width — no separate
                    centreline trace on the map.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </>
      )}

      {validationError ? (
        <p className="text-xs text-red-600 dark:text-red-400">{validationError}</p>
      ) : null}

      <button
        type="button"
        onClick={onContinue}
        className="w-full py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600"
      >
        Continue to guided marking
      </button>
    </div>
  );
}
